// File: notfound.js
//
// The "404" page. Shows up whenever someone visits a URL that doesn't
// exist in the app (because of a typo, a stale link, etc.).
//
// App.js routes the special path "*" (anything we don't recognize) to
// this page. Most edits here will be the heading text or the button.

import React from "react";
import { useNavigate } from "react-router-dom";

import logo from "./assets/512.png";
import { COLORS, THEME, pageBackground } from "./theme";

// The little house icon used on the "Back to Home" button.
function IconHome({ className = "w-5 h-5" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 10.5l9-7 9 7M5 10.5V20a2 2 0 002 2h10a2 2 0 002-2v-9.5" />
      <path d="M9 22V12h6v10" />
    </svg>
  );
}

function NotFound() {
  const navigate = useNavigate();

  return (
    <div className="flex-1 relative flex flex-col" style={pageBackground}>
      {/* --- Page header: logo on left, Home button on right --- */}
      <header
        className="sticky top-0 z-20 backdrop-blur border-b shadow-[0_6px_18px_rgba(31,56,0,0.16)]"
        style={{
          backgroundColor: THEME.headerBg,
          borderColor: "rgba(231,247,210,0.28)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/10 transition"
            aria-label="Go to home"
            type="button"
          >
            <img src={logo} alt="Logo" className="w-13 h-12 object-contain" />

            <div className="leading-tight text-left">
              <div className="text-sm font-extrabold text-white">
                H.O.P.E Navigator
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{ color: COLORS.yellowLight }}
              >
                Not found
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate("/home")}
            className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
            type="button"
          >
            Home
          </button>
        </div>
      </header>

      {/* --- The main message card. Edit the headline and paragraph
              below to change what users see on the 404 page. --- */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-4 sm:py-6 lg:py-8 flex items-center justify-center">
        <section
          className="backdrop-blur border rounded-3xl p-6 sm:p-7 lg:p-10 w-full max-w-xl text-center"
          style={{
            backgroundColor: THEME.cardBg,
            borderColor: THEME.border,
            boxShadow: THEME.shadow,
          }}
        >
          <div
            className="inline-flex items-center rounded-full px-3 py-1 text-sm font-extrabold"
            style={{ color: COLORS.brown }}
          >
            404
          </div>

          <h1
            className="text-3xl sm:text-4xl font-extrabold mt-4 leading-tight"
            style={{ color: THEME.text }}
          >
            Page not found.
          </h1>

          <p
            className="mt-3 text-base leading-7"
            style={{ color: "rgba(31,56,0,0.68)" }}
          >
            The link you followed might be broken, or the page may have moved.
          </p>

          <button
            onClick={() => navigate("/home")}
            className="mt-6 inline-flex items-center gap-2 rounded-xl px-4 py-3 text-base font-extrabold text-white transition hover:opacity-90 focus:outline-none focus:ring-4"
            style={{
              backgroundColor: COLORS.greenBase,
              boxShadow: "0 10px 20px rgba(64,115,0,0.18)",
            }}
            type="button"
          >
            <IconHome className="w-5 h-5" />
            Back to Home
          </button>
        </section>
      </main>
    </div>
  );
}

export default NotFound;
